/**
 * useClientDna — shared React Query hook for client DNA (TASK-510).
 * New ['client-dna', clientId] key (no prior fetcher) so DnaCard, Client360,
 * MeetingPrep and MeetingDnaReview dedupe to one /dna request per client.
 */

import { useQuery } from '@tanstack/react-query';
import { getClientDna, type DnaResponse } from '../services/knowledgeGraphService';

export function useClientDna(clientId: string) {
  return useQuery({
    queryKey: ['client-dna', clientId],
    queryFn: () => getClientDna(clientId),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });
}

// TASK-604: the DNA facets the client-record snapshot header renders. Pure so
// the empty-KG case is unit-testable; consumed by ClientDetail alongside DnaCard.
export interface DnaSummary {
  mandate: string | null;
  occupation: string | null;
  family: string | null;
  /** Top client values (by confidence), for the "Focus" line. */
  focus: string | null;
  redLines: string[];
  lifeEventCount: number;
  narrative: string | null;
  isEmpty: boolean;
}

export function summarizeDna(dna: DnaResponse | null | undefined): DnaSummary {
  const values = dna?.values ?? [];
  const focus =
    [...values]
      .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))
      .slice(0, 3)
      .map((v) => v.text)
      .filter(Boolean)
      .join(' · ') || null;
  const redLines = (dna?.exclusions ?? []).map((e) => e.text).filter(Boolean);
  const summary: DnaSummary = {
    mandate: dna?.mandate ?? null,
    occupation: dna?.business_context ?? null,
    family: dna?.family_context ?? null,
    focus,
    redLines,
    lifeEventCount: dna?.life_events?.length ?? 0,
    narrative: dna?.temperament ?? null,
    isEmpty: false,
  };
  summary.isEmpty =
    !summary.mandate &&
    !summary.occupation &&
    !summary.family &&
    !summary.focus &&
    summary.redLines.length === 0 &&
    summary.lifeEventCount === 0 &&
    !summary.narrative;
  return summary;
}
